import React, { useCallback } from 'react';
import { FileError } from 'react-dropzone';
import { Box } from '@common-fe/common-fe';
import styled from 'styled-components';

import { FileImage } from '@/common/constants';
import { Portal } from '@/components';
import DropScan, { DropScanProps } from '@/components/controls/DropScan';
import useIsDragging from '@/components/controls/DropScan/hooks/useIsDragging';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { USE_MY_MONEY_UPLOAD_STORE_KEY } from '@/modules/UseMyMoney/UseMyMoney.types';

import UseMyMoneyDropScanContent from './UseMyMoneyDropScanContent';

const DropWrapper = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 9999;
`;

interface Props extends DropScanProps {
  onUpload: () => void;
  deleteDocument?: (documentId: string) => void;
}

const UseMyMoneyDropScan: React.FC<Props> = ({ onUpload, deleteDocument, onFileUploadError, ...props }) => {
  const { isDragging, setIsDragging } = useIsDragging();
  const { handleReset } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);

  const handleFilesAdded = useCallback((acceptedFiles: FileImage[], currentDocumentId?: string, isError?: boolean) => {
    if (isError && currentDocumentId && deleteDocument) {
      deleteDocument(currentDocumentId);
      handleReset();
      return;
    }
    if (acceptedFiles?.length) {
      onUpload();
    }
  }, [onUpload, deleteDocument, handleReset]);

  const handleUploadError = useCallback((fileError: FileError | undefined) => {
    onFileUploadError?.(fileError);
    setIsDragging(false);
  }, [onFileUploadError, setIsDragging]);

  const contentRenderWrapper = useCallback((isLoading?: boolean) => { 
    return (
      <UseMyMoneyDropScanContent isLoading={isLoading} />
    );
  }, []);

  if (!isDragging) {
    return null;
  }

  return (
    <Portal id="page-content">
      <DropWrapper pad="spacing20">
        <Box
          background="infoContainer"
          height="100%"
          width="100%"
          round="moduleRound"
        >
          <DropScan
            onFilesAdded={handleFilesAdded}
            onFileUploadError={handleUploadError}
            wrapperProps={{ background: 'accentContainer' }}
            contentWrapperProps={{ background: 'accentContainer' }}
            contentRenderWrapper={contentRenderWrapper}
            {...props}
          />
        </Box>
      </DropWrapper>
    </Portal>
  );
};

export default UseMyMoneyDropScan;
